/**
 * Central catalog of unique per-section, per-language SEO titles & descriptions.
 * Source of truth for both runtime (SEOHead fallback) and build-time
 * (seoPrerenderPlugin) meta generation.
 *
 * Priority order applied by consumers:
 *  1. DB `seo_meta` override (admin-managed)
 *  2. This catalog (`sectionMeta`)
 *  3. Component-provided props / generic per-language default
 *
 * The raw data lives in `sectionMeta.data.json` so that non-TS build scripts
 * (verify-seo-urls.mjs) can consume the same source without a TS pipeline.
 */
import type { Language } from '@/lib/i18n/translations-meta';
import type { RouteKey } from '@/lib/i18n/routes';
import raw from './sectionMeta.data.json';

export interface SectionMetaEntry {
  title: string;
  description: string;
}

export type SectionMetaCatalog = Partial<
  Record<RouteKey, Partial<Record<Language, SectionMetaEntry>>>
>;

export const sectionMeta = raw as SectionMetaCatalog;

/**
 * Return the catalog entry for a route/language pair, or null if none defined.
 * Falls back to the English entry when the requested language is missing —
 * covers routes that are only translated later.
 */
export function getSectionMeta(
  routeKey: RouteKey | null | undefined,
  language: Language
): SectionMetaEntry | null {
  if (!routeKey) return null;
  const perLang = sectionMeta[routeKey];
  if (!perLang) return null;
  return perLang[language] || perLang.en || null;
}
